/* eslint-disable @typescript-eslint/no-unnecessary-type-parameters */
import { DecimalPipe } from '@angular/common';
import { TtNumberRangePipe } from '../../pipes/tt-number-range/tt-number-range.pipe';
import { PlainRangeInterface } from '../range.interface';
import { RangeModel } from '../range.model';
import { IntRangeInterface } from './int-range.interface';

export class IntRange<
  StartBoundary extends 'finite' | 'infinite' = 'finite',
  EndBoundary extends 'finite' | 'infinite' = 'finite',
  RangeType extends number = number,
  PlainType extends string | number = string,
  StartType extends RangeType | null = StartBoundary extends 'finite'
    ? RangeType
    : RangeType | null,
  EndType extends RangeType | null = EndBoundary extends 'finite' ? RangeType : RangeType | null,
  PlainStartType extends PlainType | null = StartBoundary extends 'finite'
    ? PlainType
    : PlainType | null,
  PlainEndType extends PlainType | null = EndBoundary extends 'finite'
    ? PlainType
    : PlainType | null,
>
  extends RangeModel<StartBoundary, EndBoundary, PlainType, RangeType, StartType, EndType>
  implements IntRangeInterface<StartBoundary, EndBoundary, RangeType, StartType, EndType>
{
  /**
   * @inheritDoc
   */
  public get min(): StartType {
    if (this.start === null) {
      return null as StartType;
    }

    if (this.inclusiveStart) {
      return this.start;
    }

    return (this.start + 1) as unknown as StartType;
  }

  /**
   * @inheritDoc
   */
  public get max(): EndType {
    if (this.end === null) {
      return null as EndType;
    }

    if (this.inclusiveEnd) {
      return this.end;
    }

    return (this.end - 1) as unknown as EndType;
  }

  /**
   * @inheritDoc
   */
  public contains(target: RangeType): boolean {
    if (this.start !== null && target < (this.min as unknown as number)) {
      return false;
    }
    if (this.end !== null && target > (this.max as unknown as number)) {
      return false;
    }
    return true;
  }

  /**
   * Instantiate the int-range class using the object
   * returned in the api response
   */
  public static override fromObject<
    PlainType extends string | number,
    D extends
      | PlainRangeInterface<'finite' | 'infinite', 'finite' | 'infinite', PlainType>
      | IntRangeInterface<'finite' | 'infinite', 'finite' | 'infinite'>,
    StartBoundary extends 'finite' | 'infinite' = D['start'] extends null ? 'infinite' : 'finite',
    EndBoundary extends 'finite' | 'infinite' = D['end'] extends null ? 'infinite' : 'finite',
    StartType extends StartBoundary extends 'finite' ? number : null | number =
      StartBoundary extends 'finite' ? number : null | number,
    EndType extends EndBoundary extends 'finite' ? number : null | number =
      EndBoundary extends 'finite' ? number : null | number,
  >(data: D) {
    const start = (
      data.start === null
        ? null
        : typeof data.start === 'string'
          ? parseInt(data.start, 10)
          : data.start
    ) as StartType;
    const end = (
      data.end === null ? null : typeof data.end === 'string' ? parseInt(data.end, 10) : data.end
    ) as EndType;
    return new IntRange<StartBoundary, EndBoundary, number, PlainType>(
      start,
      end,
      data.inclusiveStart,
      data.inclusiveEnd,
    );
  }

  /**
   * @inheritDoc
   */
  public override toObject(): PlainRangeInterface<
    StartBoundary,
    EndBoundary,
    PlainType,
    PlainStartType,
    PlainEndType
  > {
    return {
      start: this.start as unknown as PlainStartType,
      end: this.end as unknown as PlainEndType,
      inclusiveStart: this.inclusiveStart,
      inclusiveEnd: this.inclusiveEnd,
    };
  }

  /**
   * @inheritDoc
   */
  public humanReadableString(divider: string = ' - ', formatString: string = '1.0-3'): string {
    return new TtNumberRangePipe().transform(this, {
      locale: 'nl-NL',
      divider: divider,
      format: formatString,
    });
  }

  /**
   * @inheritDoc
   */
  public format(formatString: string = '1.0-3', divider: string = ' - '): string {
    const pipe = new DecimalPipe('Nl-nl');
    const start = this.start !== null ? (pipe.transform(this.start, formatString) as string) : '∞';
    const end = this.end !== null ? (pipe.transform(this.end, formatString) as string) : '∞';
    return `${start}${divider}${end}`;
  }

  /**
   * calculate how many item are in the range
   *
   * 1. determine the start value
   * @see IntRange.min
   * 2. determine the end value
   * @see IntRange.max
   *
   * example/proof:
   * :range: === :items:   === :item_count: <> :min/max_item: === :calculation:
   * [0-5] === 0 1 2 3 4 5 === 6            <> 0-5            === 5-0 = 5 +1 = 6
   * [0-5) === 0 1 2 3 4   === 5            <> 0-4            === 4-0 = 4 +1 = 5
   * (0-5] === 1 2 3 4 5   === 5            <> 1-5            === 5-1 = 4 +1 = 5
   * (0-5) === 1 2 3 4     === 4            <> 1-4            === 4-1 = 3 +1 = 4
   *
   * [1-6] === 1 2 3 4 5 6 === 6            <> 1-6            === 6-1 = 5 +1 = 6
   * [1-6) === 1 2 3 4 5   === 5            <> 1-5            === 5-1 = 4 +1 = 5
   * (1-6] === 2 3 4 5 6   === 5            <> 2-6            === 6-2 = 4 +1 = 5
   * (1-6) === 2 3 4 5     === 4            <> 2-5            === 5-2 = 3 +1 = 4
   */
  public totalSteps(): number {
    if (this.max === null || this.min === null) {
      return Infinity;
    }
    return this.max - this.min + 1;
  }

  public override toString(): string {
    const parts: string[] = [];
    parts.push(this.start === null ? '(' : this.inclusiveStart ? '[' : '(');
    // The lower bound of a range can be omitted, meaning that all values less
    // than the upper bound are included in the range, e.g., (,3].
    parts.push(this.start !== null ? this.start.toString(10) : '');
    parts.push(',');
    // Likewise, if the upper bound of the range is omitted, then all values
    // greater than the lower bound are included in the range
    parts.push(this.end !== null ? this.end.toString(10) : '');
    parts.push(this.end === null ? ')' : this.inclusiveEnd ? ']' : ')');
    return parts.join('');
  }
}
